import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, ZoomIn } from 'lucide-react';

interface ImageModalProps {
    imageUrl: string;
    onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ imageUrl, onClose }) => {
    const [isLoaded, setIsLoaded] = useState(false);

    // Handle ESC key to close
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        document.body.style.overflow = 'hidden'; // Prevent scrolling behind modal

        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
        };
    }, [onClose]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
            onClick={onClose} // Click outside to close
        >
            {/* Backdrop with heavy blur */}
            <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-2xl" />

            <div className="relative z-10 w-full h-full flex items-center justify-center">

                {/* Top Actions Bar */}
                <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center pointer-events-none">
                    <div className="flex items-center gap-3 bg-white/5 backdrop-blur-md px-4 py-2 rounded-full border border-white/10">
                        <ZoomIn size={14} className="text-amber-500" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-white/70">Archive View</span>
                    </div>

                    <button
                        onClick={onClose}
                        className="pointer-events-auto p-3 bg-white/10 hover:bg-amber-600 backdrop-blur-xl text-white rounded-full transition-all duration-300 hover:rotate-90"
                        style={{ position: 'absolute', zIndex: 1000 }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Loading Spinner */}
                {!isLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 border-2 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
                    </div>
                )}

                {/* Image Container */}
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    className="relative max-w-full max-h-full"
                    onClick={(e) => e.stopPropagation()} // Prevent closing when clicking the image
                >
                    <img
                        src={imageUrl}
                        alt="Diocesan Archive Enlargement"
                        onLoad={() => setIsLoaded(true)}
                        className={`
                            max-w-[95vw] max-h-[85vh] md:max-h-[80vh] 
                            rounded-2xl shadow-[0_0_80px_rgba(0,0,0,0.5)] 
                            border border-white/10 object-contain
                            transition-opacity duration-500
                            ${isLoaded ? 'opacity-100' : 'opacity-0'}
                        `}
                    />

                    {/* Image Footer Info */}
                    <div className="absolute -bottom-12 left-0 right-0 flex justify-center">
                        <button
                            onClick={() => window.open(imageUrl, '_blank')}
                            className="flex items-center gap-2 text-white/40 hover:text-amber-500 transition-colors text-[10px] font-bold uppercase tracking-widest"
                        >
                            <Download size={14} />
                            Full Resolution
                        </button>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default ImageModal;